"use client";

import type React from "react";

import { useState, useRef } from "react";
import { findFieldById, createDefaultField } from "@/utils/schema-utils";
import {
  DragState,
  SchemaField,
} from "@papier/grapesjs-editor/src/components/editor/schema-builder/types/schema";

export const useDragDrop = (
  schema: SchemaField[],
  moveField: (draggedId: string, targetId: string, position: "before" | "after" | "inside") => void,
  addFieldToParent: (
    fields: SchemaField[],
    parentId: string,
    newField: SchemaField,
  ) => SchemaField[],
  setSchema: React.Dispatch<React.SetStateAction<SchemaField[]>>,
) => {
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    draggedField: null,
    draggedFieldType: null,
    dropZone: null,
  });
  const dragCounter = useRef(0);

  const resetDragState = () => {
    setDragState({
      isDragging: false,
      draggedField: null,
      draggedFieldType: null,
      dropZone: null,
    });
    dragCounter.current = 0;
  };

  const handleDragStart = (e: React.DragEvent, field: SchemaField) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", field.id);
    e.dataTransfer.setData(
      "application/json",
      JSON.stringify({ type: "existing", fieldId: field.id }),
    );
    setDragState({
      isDragging: true,
      draggedField: field,
      draggedFieldType: null,
      dropZone: null,
    });
    dragCounter.current = 0;
  };

  const handleSidebarDragStart = (e: React.DragEvent, fieldType: SchemaField["type"]) => {
    e.dataTransfer.effectAllowed = "copy";
    e.dataTransfer.setData("text/plain", `new:${fieldType}`);
    e.dataTransfer.setData("application/json", JSON.stringify({ type: "new", fieldType }));
    setDragState({
      isDragging: true,
      draggedField: null,
      draggedFieldType: fieldType,
      dropZone: null,
    });
    dragCounter.current = 0;
  };

  const handleDragEnter = (
    e: React.DragEvent,
    targetId: string,
    position: "before" | "after" | "inside",
  ) => {
    e.preventDefault();
    e.stopPropagation();

    if (position === "inside") {
      const targetField = findFieldById(schema, targetId);
      if (targetField?.type !== "object") {
        return;
      }
    }

    setDragState((prev) => ({
      ...prev,
      isDragging: true,
      dropZone: { id: targetId, position },
    }));
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.stopPropagation();
    setTimeout(() => {
      setDragState((prev) => ({
        ...prev,
        dropZone: null,
      }));
    }, 50);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragState((prev) => ({
      ...prev,
      isDragging: false,
    }));
  };

  const handleDrop = (
    e: React.DragEvent,
    targetId?: string,
    position?: "before" | "after" | "inside",
  ) => {
    e.preventDefault();
    e.stopPropagation();

    const dragData = e.dataTransfer.getData("text/plain");

    if (dragData.startsWith("new:")) {
      const fieldType = dragData.replace("new:", "") as SchemaField["type"];
      const newField = createDefaultField(fieldType);

      if (!targetId) {
        setSchema((prev) => [...prev, newField]);
      } else if (position === "inside") {
        setSchema((prev) => addFieldToParent(prev, targetId, newField));
      } else {
        const insertAtPosition = (fields: SchemaField[]): SchemaField[] => {
          const targetIndex = fields.findIndex((f) => f.id === targetId);
          if (targetIndex !== -1) {
            const insertIndex = position === "before" ? targetIndex : targetIndex + 1;
            const newFields = [...fields];
            newFields.splice(insertIndex, 0, newField);
            return newFields;
          }
          return fields.map((field) => ({
            ...field,
            objectFields: field.objectFields ? insertAtPosition(field.objectFields) : undefined,
          }));
        };
        setSchema((prev) => insertAtPosition(prev));
      }
    } else {
      const draggedId = dragData;
      if (draggedId && targetId && draggedId !== targetId && position) {
        moveField(draggedId, targetId, position);
      }
    }

    resetDragState();
  };

  return {
    dragState,
    dragCounter,
    handleDragStart,
    handleSidebarDragStart,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    resetDragState,
  };
};
